import type {
  Block,
  BlockType,
  Deck,
  GenerationOptions,
  OutlineSection,
  SectionPlan,
  StudioStep,
  TalkBrief,
} from "@hali-shares/deck-schema";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const form = body instanceof FormData;
  const res = await fetch(`/api${path}`, {
    method,
    credentials: "same-origin",
    headers: body === undefined || form ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : form ? body : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const message = (data as { message?: string } | undefined)?.message ?? res.statusText;
    throw new ApiError(res.status, message, data);
  }
  return data as T;
}

export type DeckSummary = { id: string; title: string; updatedAt: string };
export type DeckSettings = {
  generation?: GenerationOptions;
  brief?: TalkBrief;
  plan?: Record<string, SectionPlan>;
  step?: StudioStep;
};
export type DeckRecord = DeckSummary & {
  spec: Deck;
  source: string;
  version: number;
  settings?: DeckSettings;
  archivedAt?: string | null;
};
/** A row in the talk list. */
export type DeckListItem = DeckSummary & {
  presenter: string;
  sections: number;
  slides: number;
  minutes: number;
  version: number;
  liveCode: string | null;
  archivedAt: string | null;
};
export type { GenerationOptions } from "@hali-shares/deck-schema";

export type StartedSession = {
  code: string;
  presenterToken: string;
  deckVersion: number;
  spec: Deck;
  status: "LIVE" | "ENDED";
};
export type Issue = { path: string; message: string };
export type SessionSummary = {
  code: string;
  status: "LIVE" | "ENDED";
  createdAt: string;
  endedAt: string | null;
  deckVersion: number;
  participants: number;
  votes: number;
};
export type Version = { number: number; note: string | null; createdAt: string };
export type Asset = {
  id: string;
  /** null: uploaded before images belonged to a talk; any talk can claim it. */
  deckId?: string | null;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
};
export type PreferenceKey = "theme" | "studio-thumbs" | "preview-open";
export type TimerRun = { id: string; times: Record<string, number>; totalMs: number } | null;

export type StatsRun = { totalMs: number; plannedMs: number; at: string };
export type TalkStats = {
  sessions: number;
  lastPresentedAt: string | null;
  people: number;
  avgPeople: number | null;
  responses: number;
  responseRate: number | null;
  quizAccuracy: number | null;
  rehearsals: number;
  lastRehearsal: StatsRun | null;
  lastLive: StatsRun | null;
  toFix: number;
  toCheck: number;
};
export type SessionStats = {
  code: string;
  deckId: string;
  deckTitle: string;
  status: "LIVE" | "ENDED";
  createdAt: string;
  endedAt: string | null;
  people: number;
  responses: number;
  responseRate: number | null;
  talkMs: number | null;
  /** Each question that got answers, in slide order: how many of the people who joined answered it. */
  questions: { blockId: string; question: string; type: "poll" | "quiz"; answered: number }[];
};
export type Insight = {
  kind: string;
  tone: "good" | "neutral" | "attention";
  title: string;
  detail: string;
  deckId?: string;
};
export type Stats = {
  totals: {
    talks: number;
    sessions: number;
    people: number;
    responses: number;
    responseRate: number | null;
    quizAccuracy: number | null;
    rehearsals: number;
    rehearsalMs: number;
  };
  weeks: { start: string; sessions: number; people: number }[];
  talks: Record<string, TalkStats>;
  recent: SessionStats[];
  insights: Insight[];
};
export type Enriched = { url: string; title: string; authors?: string; venue?: string; year?: number; ok: boolean };

/** Validation issues from a 422, if that's what this error is. */
export function issuesOf(e: unknown): Issue[] {
  return e instanceof ApiError ? ((e.body as { issues?: Issue[] } | undefined)?.issues ?? []) : [];
}

export type JoinInfo = { code: string; status: "LIVE" | "ENDED"; deckTitle: string; presenter: string };

export const api = {
  login: (password: string) => call<void>("POST", "/auth/login", { password }),
  logout: () => call<void>("POST", "/auth/logout"),
  me: () => call<{ admin: boolean }>("GET", "/auth/me"),
  config: () => call<{ publicUrl: string }>("GET", "/config"),
  decks: () => call<DeckListItem[]>("GET", "/decks"),
  archivedDecks: () => call<DeckListItem[]>("GET", "/decks?archived=true"),
  unarchiveDeck: (id: string) => call<DeckRecord>("POST", `/decks/${id}/unarchive`),
  duplicateDeck: (id: string) => call<DeckRecord>("POST", `/decks/${id}/duplicate`),
  deck: (id: string) => call<DeckRecord>("GET", `/decks/${encodeURIComponent(id)}`),
  importDeck: (spec: unknown) => call<DeckRecord>("POST", "/decks/import", spec),
  createDeck: (title: string) => call<DeckRecord>("POST", "/decks", { title }),
  /** `baseVersion`: the version this edit started from; the server answers 409 if the deck moved on. */
  updateDeck: (id: string, spec: Deck, note?: string, baseVersion?: number) =>
    call<DeckRecord>("PUT", `/decks/${id}`, { spec, note, baseVersion }),
  saveSource: (id: string, source: string) => call<{ ok: true }>("PUT", `/decks/${id}/source`, { source }),
  saveSettings: (id: string, settings: DeckSettings) => call<DeckSettings>("PUT", `/decks/${id}/settings`, settings),
  /** Archives: the talk and its history stay in the database. */
  archiveDeck: (id: string) => call<void>("DELETE", `/decks/${id}`),
  versions: (id: string) => call<Version[]>("GET", `/decks/${id}/versions`),
  restore: (id: string, n: number) => call<DeckRecord>("POST", `/decks/${id}/versions/${n}/restore`),
  generationStatus: () => call<{ provider: string; configured: boolean; vision: boolean }>("GET", "/generation/status"),
  placePins: (id: string) => call<DeckRecord>("POST", `/decks/${id}/place-pins`),
  /** `followOutline`: keep the deck's sections (the presenter's outline) and write their slides. */
  generate: (id: string, source: string, minutes?: number, options?: GenerationOptions, followOutline?: boolean) =>
    call<DeckRecord>("POST", `/decks/${id}/generate`, { source, minutes, options, followOutline }),
  /** A suggested outline (or `current` changed as instructed). Saves nothing. */
  suggestOutline: (id: string, instruction: string, current?: OutlineSection[]) =>
    call<{ minutes: number; sections: (OutlineSection & { id: string })[] }>("POST", `/decks/${id}/outline/suggest`, {
      instruction,
      current,
    }),
  rewriteBlock: (id: string, deck: Deck, blockId: string, instruction: string, type?: BlockType) =>
    call<{ block: Block }>("POST", `/decks/${id}/blocks/rewrite`, { deck, blockId, instruction, type }),
  regenerateSection: (id: string, sectionId: string, instruction: string) =>
    call<DeckRecord>("POST", `/decks/${id}/sections/${encodeURIComponent(sectionId)}/regenerate`, { instruction }),
  enrich: (url: string) => call<Enriched>("POST", "/references/enrich", { url }),
  assets: (deckId: string) => call<Asset[]>("GET", `/assets?deckId=${encodeURIComponent(deckId)}`),
  uploadAsset: (file: File, deckId: string) => {
    const form = new FormData();
    form.append("deckId", deckId); // before the file, so the server reads it first
    form.append("file", file);
    return call<Asset>("POST", "/assets", form);
  },
  deleteAsset: (id: string) => call<void>("DELETE", `/assets/${id}`),
  assignAsset: (id: string, deckId: string) => call<Asset>("PATCH", `/assets/${id}`, { deckId }),
  endSession: (code: string) =>
    call<{ code: string; status: string }>("POST", `/sessions/${encodeURIComponent(code)}/end`),
  stats: () => call<Stats>("GET", "/stats"),
  preferences: () => call<Partial<Record<PreferenceKey, unknown>>>("GET", "/preferences"),
  setPreference: (key: PreferenceKey, value: unknown) => call<unknown>("PUT", `/preferences/${key}`, { value }),
  timer: (deckId: string, session: string | null) =>
    call<TimerRun | "">("GET", `/decks/${deckId}/timer${session ? `?session=${encodeURIComponent(session)}` : ""}`),
  /** `runId`: the run these times belong to; the server ignores a save for a run that was reset meanwhile. */
  saveTimer: (
    deckId: string,
    session: string | null,
    times: Record<string, number>,
    runId: string | null,
    keepalive = false,
  ) =>
    fetch(`/api/decks/${deckId}/timer`, {
      method: "PUT",
      credentials: "same-origin",
      keepalive, // still sent when the tab is closing
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...(session && { session }), ...(runId && { runId }), times }),
    }).then(async (r) => {
      if (!r.ok) throw new ApiError(r.status, r.statusText);
      return (await r.json()) as { id: string; stale?: true };
    }),
  /** Finishes the open run with its final times, in one call. */
  resetTimer: (deckId: string, session: string | null, times?: Record<string, number>) =>
    call<{ finished: number }>("POST", `/decks/${deckId}/timer/reset`, {
      ...(session && { session }),
      ...(times && { times }),
    }),
  sessionsFor: (deckId: string) => call<SessionSummary[]>("GET", `/sessions?deckId=${encodeURIComponent(deckId)}`),
  startSession: (deckId: string) => call<StartedSession>("POST", "/sessions", { deckId }),
  joinSession: (code: string) => call<JoinInfo>("POST", `/sessions/${encodeURIComponent(code)}/join`),
  sessionMe: (code: string) => call<JoinInfo & { joined: boolean }>("GET", `/sessions/${encodeURIComponent(code)}/me`),
};
